import { useTranslation } from 'react-i18next';
import { Workplace } from 'src/api/wa/store/models/workplace';
import { getDetailStatusTranslationKey } from 'src/api/wa/store/models/workplace/OperationStatus';
import Cell from 'src/ui/wards/common/cell/Cell';

export enum SiteDetailsSection {
  information = 'information',
  disasterInformation = 'disasterInformation',
}

type SiteDetailsProps = {
  site: Workplace & { clientNumber?: string };
  section: SiteDetailsSection;
};

export function DetailsList({ site, section }: SiteDetailsProps) {
  const { t } = useTranslation();

  if (!site) {
    return null;
  }

  const informationItems = [
    { key: 'placeOfWork', value: site.name },
    { key: 'branchOfficeName', value: site.branchName },
    { key: 'organizationCode', value: site.organizationCode },
    { key: 'customerNumber', value: site.customerId ?? site.clientNumber },
    { key: 'telephoneNumberDayNormal', value: site.dayPhoneNumber, isPhoneNumber: true },

    {
      key: 'telephoneNumberDayPrefix',
      value: site.dayPhoneNumber ? `710-${site.dayPhoneNumber}` : undefined,
      isPhoneNumber: true,
    },

    { key: 'telephoneNumberNightNormal', value: site.nightPhoneNumber, isPhoneNumber: true },
    {
      key: 'telephoneNumberNightPrefix',
      value: site.nightPhoneNumber ? `710-${site.nightPhoneNumber}` : undefined,
      isPhoneNumber: true,
    },

    { key: 'emergencyClass', value: site.emergencyClass },

    { key: 'customerAddressPostOffice', value: site.postalCode },
    { key: 'customerAddress', value: site.address, isGoogleMapAddress: true },
    { key: 'remarks', value: site.remarks },
    { key: 'remarkSupplElevator', value: site.remarkSupplElevator },
    { key: 'procedureSupplElevator', value: site.procedureSupplElevator },
    { key: 'remarkSupplHeatpump', value: site.remarkSupplHeatpump },
    { key: 'procedureSupplHeatpump', value: site.procedureSupplHeatpump },
    { key: 'entryProcedure', value: site.entryProcedure },
  ];

  const disasterItems = [
    { key: 'disasterId', value: site.details?.disasterId },
    { key: 'state', value: site.details?.statusId, isStatusId: true },
    { key: 'dispatchedWorker', value: site.details?.reservations[0]?.engineerName },
    { key: 'comment', value: site.details?.comment },
  ];

  return (
    <ul className="space-y-2">
      {section === SiteDetailsSection.information &&
        informationItems
          .filter(({ value }) => value !== undefined && value !== null && value !== '')
          .map(({ key, value, isPhoneNumber, isGoogleMapAddress }) => (
            <Cell
              key={key}
              placeholder={t(`wards.siteDetails.${key}`)}
              value={String(value)}
              className="break-all border-b border-gray-300"
              {...(isPhoneNumber && { phoneNumber: String(value) })}
              {...(isGoogleMapAddress && { googleMapAddress: String(value) })}
            />
          ))}

      {section === SiteDetailsSection.disasterInformation &&
        disasterItems.map(({ key, value, isStatusId }) => {
          const displayValue = isStatusId
            ? t(getDetailStatusTranslationKey(value))
            : value
              ? String(value)
              : '';
          return (
            <Cell
              key={key}
              placeholder={t(`wards.siteDetails.${key}`)}
              value={displayValue}
              className="break-all border-b border-gray-300"
            />
          );
        })}
    </ul>
  );
}
